import { useState, useMemo } from 'react';
import { useValuation } from '@/hooks/useValuation';
import { useValuationSummary } from '@/hooks/useValuationSummary';
import { ValuationTable } from '@/components/ValuationTable';
import { ValuationDrawer } from '@/components/ValuationDrawer';
import { ValuationMetrics } from '@/types/valuation';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { AlertCircleIcon, Calculator, InfoIcon } from 'lucide-react';

const Valuation = () => {
  const { data: fullData, isLoading: fullLoading, error: fullError } = useValuation();
  const { data: summaryData, isLoading: summaryLoading } = useValuationSummary();
  const [selectedAsset, setSelectedAsset] = useState<ValuationMetrics | null>(null);

  // Use full data if available, fallback to summary data with calculated ratios
  const { data, isLoading, isUsingFallback } = useMemo(() => {
    if (fullData && !fullError) {
      console.info('Valuation: Using full data with', fullData.items?.length || 0, 'assets');
      return { 
        data: fullData, 
        isLoading: fullLoading, 
        isUsingFallback: false 
      };
    }
    
    if (summaryData && fullError) {
      console.info('Valuation: Using summary fallback with', summaryData.items?.length || 0, 'assets');
      // Convert summary data to full valuation format with calculated ratios
      const enhancedItems: ValuationMetrics[] = summaryData.items.map(item => ({
        slug: item.slug,
        symbol: item.symbol,
        name: item.name,
        price: item.price,
        market_cap: item.market_cap,
        fdv: item.market_cap, // Approximation
        rank: item.rank,
        change_24h: item.change_24h,
        change_7d: 0, // Not available in summary
        image: item.image,
        tvl: item.tvl,
        fees_7d: item.fees_7d,
        revenue_7d: item.revenue_7d,
        fees_annualized: item.fees_7d * 52,
        revenue_annualized: item.revenue_7d * 52,
        // Calculate ratios client-side
        p_fees: item.fees_7d > 0 ? item.market_cap / (item.fees_7d * 52) : null,
        p_revenue: item.revenue_7d > 0 ? item.market_cap / (item.revenue_7d * 52) : null,
        fdv_fees: item.fees_7d > 0 ? item.market_cap / (item.fees_7d * 52) : null,
        fdv_revenue: item.revenue_7d > 0 ? item.market_cap / (item.revenue_7d * 52) : null,
        fees_tvl: (item.tvl && item.fees_7d > 0) ? (item.fees_7d * 52) / item.tvl * 100 : null,
        revenue_tvl: (item.tvl && item.revenue_7d > 0) ? (item.revenue_7d * 52) / item.tvl * 100 : null,
        deltas: { tvl_7d: 0, fees_7d: 0, revenue_7d: 0 },
        data_flags: {
          has_tvl: item.has_tvl,
          has_fees: item.has_fees,
          has_revenue: item.has_revenue,
          tvl_source: item.tvl_source,
          tvl_source_label: item.tvl_source_label,
          uses_proxy_tvl: false,
        },
        protocol_name: item.name,
        category: 'defi'
      }));
      
      return { 
        data: { 
          ...summaryData, 
          items: enhancedItems 
        }, 
        isLoading: summaryLoading, 
        isUsingFallback: true 
      };
    }
    
    return { 
      data: null, 
      isLoading: fullLoading || summaryLoading, 
      isUsingFallback: false 
    };
  }, [fullData, fullError, summaryData, fullLoading, summaryLoading]);

  if (fullError && !summaryData) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert variant="destructive" className="glass-card">
          <AlertCircleIcon className="h-4 w-4" />
          <AlertDescription>
            Failed to load valuation data. Please try again later.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="space-y-6">
        {/* Hero Section */}
        <div className="space-y-3 fade-in-up">
          <div className="flex items-center gap-4 flex-wrap">
            <h1 className="text-4xl font-bold tracking-tight text-gradient">
              Crypto Valuation
            </h1>
            <Badge className="bg-primary/10 text-primary border-primary/20">
              <Calculator className="h-3 w-3 mr-1" />
              {data?.items?.length || 0} Protocolos
            </Badge>
            {isUsingFallback && (
              <Badge variant="outline" className="flex items-center gap-1">
                <InfoIcon className="h-3 w-3" />
                Cache
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground text-lg max-w-3xl">
            Métricas fundamentalistas combinando dados de mercado com DeFi.
            Analise P/Fees, P/Revenue e eficiência de capital.
          </p>
          <div className="gradient-line w-24" />
        </div>

        <div className="fade-in-up delay-100">
          <ValuationTable 
            data={data?.items || []}
            isLoading={isLoading}
            onAssetSelect={setSelectedAsset}
            lastUpdated={data?.updated_at}
          />
        </div>

        <ValuationDrawer
          asset={selectedAsset}
          isOpen={!!selectedAsset}
          onClose={() => setSelectedAsset(null)}
        />
      </div>
    </div>
  );
};

export default Valuation;
